/**
 * System Settings Page - Sprint 13
 * View-only page showing system configuration for Admin/ComplianceManager.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { RefreshCw, Mail, Shield, Clock, Archive, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const SystemSettingsPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  const canAccess = user?.role === 'Admin' || user?.role === 'ComplianceManager';

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/system/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load system settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && canAccess) {
      fetchSettings();
    } else if (!authLoading && !canAccess) {
      // If user doesn't have access, stop loading
      setLoading(false);
    }
  }, [authLoading, canAccess, fetchSettings]);

  const handleManualCleanup = async () => {
    if (user?.role !== 'Admin') {
      toast.error('Admin access required');
      return;
    }

    try {
      setCleanupLoading(true);
      const response = await api.post('/system/cleanup-exports');
      toast.success(response.data.message);
      fetchSettings(); // Refresh settings after cleanup
    } catch (error) {
      console.error('Cleanup error:', error);
      toast.error('Failed to run export cleanup');
    } finally {
      setCleanupLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-600" />
      </div>
    );
  }

  if (!canAccess) {
    return (
      <Layout title="System Settings">
        <Card className="max-w-md mx-auto mt-12">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-600 mb-4">Admin or Compliance Manager access required.</p>
            <Button onClick={() => navigate('/')} variant="outline">Go Back</Button>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  if (!settings) {
    return null;
  }

  return (
    <Layout title="System Settings">
      <div data-testid="system-settings-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-600">View-only system configuration. Contact administrator to change settings.</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchSettings} disabled={loading}>
            <RefreshCw className={loading ? 'h-4 w-4 mr-2 animate-spin' : 'h-4 w-4 mr-2'} />
            Refresh
          </Button>
        </div>

        {/* Email Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-avida-blue" />
              Email Configuration
            </CardTitle>
            <CardDescription>Email notification settings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-slate-500">Email Notifications</p>
                <Badge className={settings.email.enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                  {settings.email.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-slate-500">Resend API</p>
                <Badge className={settings.email.resend_configured ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                  {settings.email.resend_configured ? 'Configured' : 'Not Configured'}
                </Badge>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-slate-500">From Address</p>
                <p className="font-mono text-sm">{settings.email.from_address}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-slate-500">Office Contact</p>
                <p className="text-sm">{settings.email.office_contact_email}</p>
                <p className="text-sm text-slate-500">{settings.email.office_contact_phone}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Authentication Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-avida-blue" />
              Authentication Settings
            </CardTitle>
            <CardDescription>Token and session configuration</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-500">Access Token TTL</p>
                <p className="text-2xl font-semibold text-slate-900">{settings.auth.access_token_ttl_minutes}</p>
                <p className="text-xs text-slate-400">minutes</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-500">Refresh Token TTL</p>
                <p className="text-2xl font-semibold text-slate-900">{settings.auth.refresh_token_ttl_days}</p>
                <p className="text-xs text-slate-400">days</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-500">Session Idle Timeout</p>
                <p className="text-2xl font-semibold text-slate-900">{settings.auth.session_idle_timeout_minutes}</p>
                <p className="text-xs text-slate-400">minutes</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-500">Password Reset TTL</p>
                <p className="text-2xl font-semibold text-slate-900">{settings.auth.password_reset_token_ttl_minutes}</p>
                <p className="text-xs text-slate-400">minutes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Export Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-avida-blue" />
              Export Settings
            </CardTitle>
            <CardDescription>Survey export configuration</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-500">Retention Period</p>
                <p className="text-2xl font-semibold text-slate-900">{settings.exports.retention_days}</p>
                <p className="text-xs text-slate-400">days</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-500">Max Date Range</p>
                <p className="text-2xl font-semibold text-slate-900">{settings.exports.max_range_days}</p>
                <p className="text-xs text-slate-400">days</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-500">Max File Count</p>
                <p className="text-2xl font-semibold text-slate-900">{settings.exports.max_file_count}</p>
                <p className="text-xs text-slate-400">per export</p>
              </div>
            </div>
            {user?.role === 'Admin' && (
              <div className="border-t pt-4">
                <Button 
                  variant="outline" 
                  onClick={handleManualCleanup} 
                  disabled={cleanupLoading}
                  className="w-full md:w-auto"
                >
                  {cleanupLoading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Archive className="h-4 w-4 mr-2" />
                  )}
                  Run Manual Cleanup
                </Button>
                <p className="text-xs text-slate-500 mt-2">
                  Purges exports older than {settings.exports.retention_days} days
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rate Limits */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-avida-blue" />
              Rate Limits
            </CardTitle>
            <CardDescription>API rate limiting configuration</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4 font-medium text-slate-600">Endpoint</th>
                    <th className="text-left py-2 px-4 font-medium text-slate-600">Limit</th>
                    <th className="text-left py-2 px-4 font-medium text-slate-600">Current Usage</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(settings.rate_limits).map(([key, config]) => (
                    <tr key={key} className="border-b last:border-0">
                      <td className="py-3 px-4">
                        <span className="font-mono text-slate-900">{key}</span>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline">{config.window_description}</Badge>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-slate-600">
                          {settings.rate_limit_stats[key]?.current_entries || 0} active entries
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* System Health Indicators */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              System Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <div className={`h-3 w-3 rounded-full ${settings.email.enabled && settings.email.resend_configured ? 'bg-green-500' : 'bg-amber-500'}`}></div>
                <span className="text-sm">Email Service</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-green-500"></div>
                <span className="text-sm">Database</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-green-500"></div>
                <span className="text-sm">Rate Limiting</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-green-500"></div>
                <span className="text-sm">Export Cleanup</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default SystemSettingsPage;
